import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useOrganization, isPlanActive } from '@/hooks/useOrganization';

const DEFAULT_PLANS = ['guionista', 'indie', 'pase', 'line-producer', 'productora', 'estudio'];

interface PlanGateProps {
  plans?: string[];
  children: ReactNode;
}

// Candado de acceso: si la org no tiene un plan activo, muestra el bloqueo con un
// único CTA a /plan (la escalera real con los Stripe Payment Links configurados).
// Antes apuntaba a env VITE_GUIONISTA_LINK_* (sin configurar) → "Próximamente"
// permanente = el usuario no podía pagar desde el candado.
export default function PlanGate({ plans = DEFAULT_PLANS, children }: PlanGateProps) {
  // El proyecto abierto viaja en la URL (?job=). Manda el plan de la organización DUEÑA
  // de ese proyecto, igual que en el backend: un colaborador invitado usa las funciones
  // de la productora que le invitó EN los proyectos de esa productora.
  const [searchParams] = useSearchParams();
  const { data: org } = useOrganization(searchParams.get('job'));

  if (isPlanActive(org, plans)) {
    return <>{children}</>;
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <Card className="max-w-md w-full border-pelip-border bg-pelip-bg-elev">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-3">
            <Lock size={32} className="text-pelip-text-faint" />
          </div>
          <CardTitle className="text-lg text-pelip-text">
            Necesitas un plan activo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <p className="text-sm text-pelip-text-muted leading-relaxed">
            Esta sección requiere una membresía activa. Elige el plan que encaje con
            tu proyecto para desbloquear todas las herramientas de desarrollo.
          </p>
          <div className="pt-2">
            <Button asChild className="w-full">
              <Link to="/plan">Ver planes y precios</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
